import { vehicleModelApi } from "#/api/vehicleModelApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import type { VehicleModel } from "#/types/vehicle-model";
import { Button, Modal, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const VehicleModelsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [vehicleModelToDelete, setVehicleModelToDelete] =
    useState<VehicleModel>();

  const headers = [
    { key: "id", value: t("vehicleModels.columns.id") },
    { key: "name", value: t("vehicleModels.columns.name") },
    { key: "brand", value: t("vehicleModels.columns.brand") },
    { key: "model", value: t("vehicleModels.columns.model") },
    { key: "size", value: t("vehicleModels.columns.size") },
    { key: "updatedAt", value: t("vehicleModels.columns.lastUpdated") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getVehicleModels,
  } = useQuery({
    queryKey: ["vehicle-models", sortBy, page, sortBy, sortDirection],
    queryFn: async () => {
      try {
        const response = await vehicleModelApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No vehicle models returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  const deleteVehicleModelMutation = useMutation({
    mutationFn: async (id: number) => {
      return await vehicleModelApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("vehicleModels.deleteModel.success"),
      });
      closeDeleteModal();
      setVehicleModelToDelete(undefined);
      void getVehicleModels();
    },
    onError: (error) => {
      console.log("🚀 ~ deleteVehicleModel ~ error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("vehicleModels.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                void navigate("/fleet/models/new");
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("vehicleModels.createNewModel")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isPending}
          allowSorting={true}
          sortableColumns={[1, 2, 3, 4, 5]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 5,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td style={{ minWidth: '100px' }}>{item.id ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>{item.name ?? "--"}</Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '150px' }}>{item.brand ?? "--"}</Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '150px' }}>{item.model ?? "--"}</Table.Td>
                <Table.Td className="capitalize" style={{ minWidth: '120px' }}>{item.size ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>
                  {item.updatedAt ? (
                    <span>
                      {formatDate(item.updatedAt, client)}
                      <br />
                      {formatTime(item.updatedAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '120px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/fleet/models/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setVehicleModelToDelete(item);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        title={t("vehicleModels.deleteModel.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col gap-2 text-sm">
            <span>{t("vehicleModels.deleteModel.description")}</span>
            <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{vehicleModelToDelete?.name ?? "--"}</span>
              <span className="text-xs text-gray-600">
                {vehicleModelToDelete?.brand} - {vehicleModelToDelete?.model} ({vehicleModelToDelete?.size})
              </span>
            </div>
            <span>
              {t("vehicleModels.deleteModel.warning")}
            </span>

            <span className="font-bold my-2">
              {t("vehicleModels.deleteModel.confirmation")}
            </span>
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (vehicleModelToDelete) {
                    deleteVehicleModelMutation.mutate(vehicleModelToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteVehicleModelMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete()}
              >
                {t("vehicleModels.deleteModel.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VehicleModelsPage;
